<template>
	<div class="p-mb-2 p-p-2">
		<BlockUI :blocked="loading">
			<UserForm :user="user" :showErrorMessage="showValidationMessage" :userId="userId" @update="onUpdate" />
			<div v-if="!user.externalUser" class="p-col-12">
				<div class="p-field p-input-filled p-inputgroup">
					<InputText v-show="showCopyLink" id="tokenLinkRef" v-model.trim="tokenLink" required="true" ref="tokenLinkRef" />
					<Button v-show="showCopyLink" icon="mdi mdi-content-copy"  @click="onClickCopy" />
				</div>
			</div>
			<div v-if="!user.externalUser" class="p-fluid p-formgrid p-grid p-jc-end p-p-2">
				<div v-if="store.state.isAdminUser === true && store.state.publicAuthConfig.basicAuthentication" class="p-field p-col-3">
					<Button
						:label="$t('Squeeze.UserManagement.ResetPassword')"
						type="button"
						icon="mdi mdi-lock-reset"
						class="p-button"
						@click="openResetDialog"
						:disabled="!userId"
					/>
				</div>
				<div class="p-field p-col-3">
					<Button
						:label="$t('Squeeze.General.Save')"
						type="button"
						icon="mdi mdi-content-save-outline"
						class="p-button"
						@click="saveUser"
					/>
				</div>
			</div>
		</BlockUI>
	</div>
	<EntryDialog
		:show="showDialog"
		@onClose="showDialog = false"
		@onConfirm="sendResetMail"
		:loading="loadingDialog"
		:save-button-text="$t('Squeeze.Login.Send')"
		:headerText="$t('Squeeze.UserManagement.ResetPassword')"
	>
		<template #content>
			<Message severity="info" :closable="false">
				<p>{{$t('Squeeze.UserManagement.ResetPasswordInfo')}}</p>
			</Message>
		</template>
	</EntryDialog>
</template>

<script lang="ts">
import {
	defineComponent, onMounted, reactive, ref
} from 'vue';
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import BlockUI from "primevue/blockui";
import {User} from "@dex/squeeze-client-ts";
import {ToastManager} from "@/util/ToastManager";
import {ClientManager} from "@/singletons/ClientManager";
import UserForm from "@/apps/administration/components/users/UserForm.vue";
import router from "@/router";
import EntryDialog from "@/components/EntryDialog.vue";
import Message from "primevue/message";
import {useSqueezeStore} from "@/apps/squeeze/store";
import InputText from "primevue/inputtext";

export default defineComponent({
	name: "UserView",
	components: {
		UserForm, BlockUI, EntryDialog, Message, InputText,
	},
	props: {
		userId: {
			type: Number,
			default: null,
		},
	},
	emits: [],
	setup(props) {
		const {t} = useI18n();
		const toast = useToast();
		const store = useSqueezeStore();

		const tokenLinkRef = ref<any>()

		/** Show Loading on load data */
		const loading = ref(false);

		/** Service for getting the user-data */
		const userService = ClientManager.getInstance().squeeze.user;

		/** Currently active user */
		const user = reactive<User>({
			login: '',
			firstName: '',
			lastName: '',
			email: '',
			password: '',
			externalUser: false,
		})

		/** Should the dialog be shown? */
		const showDialog = ref(false);

		/** Should the loading in the dialog be shown? */
		const loadingDialog = ref(false);

		/** Is the Form of the master data invalid? */
		const isValidationInvalid = ref(true);

		/** Show error validation-messages in form? */
		const showValidationMessage = ref(false);

		/** Service for public requests */
		const publicApi = ClientManager.getInstance().squeeze.public;

		/** Show the link to copy token? */
		const showCopyLink = ref(false);

		/** Link for Reset token */
		const tokenLink = ref('');

		/** Triggered on update of attribute-form */
		const onUpdate = (data: User, isInvalid: boolean) => {
			isValidationInvalid.value = isInvalid;
			Object.assign(user, data)
		}

		/** Reloads the data */
		const reloadData = () => {
			if (props.userId) {
				loading.value = true;
				userService.getUserById(props.userId)
					.then((data: User) => {
						Object.assign(user, data);
					})
					.catch((response: any) => response.json().then((err: { message: string }) => {
						ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
					}))
					.finally(() => {
						loading.value = false;
					})
			}
		}

		onMounted(() => {
			reloadData();
		})

		/** Saves an user */
		const saveUser = () => {
			if (isValidationInvalid.value) {
				showValidationMessage.value = true;
				return;
			}
			showValidationMessage.value = false;
			loading.value = true;

			if (!user.password) {
				user.password = undefined;
			}

			if (!user.id) {
				userService.addUser(user)
					.then((data) => {
						ToastManager.showSuccess(toast, t('Squeeze.General.Success'), t('Squeeze.General.CreateSuccess'));
						if (data.id) {
							router.push({ name: 'UserTabsView', params: { userId: data.id }});
							user.id = data.id;
						}
					}).catch(response => response.json().then ((err: { message: string }) => {
						ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
					})).finally(() => {
						loading.value = false;
					});
			} else {
				userService.updateUser(user.id, user).then(() => {
					ToastManager.showSuccess(toast, t('Squeeze.General.Success'), t('Squeeze.General.SaveSuccess'));
				}).catch(response => response.json().then ((err: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
				})).finally(() => {
					loading.value = false;
				});
			}
		}

		/** Opens the reset-dialog */
		const openResetDialog = () => {
			showDialog.value = true;
		}

		/** Send Reset Mail */
		const sendResetMail = () => {
			loadingDialog.value = true;
			publicApi.sendResetPasswortEmail(user.login!).then((data) => {
				if (data.value) {
					showCopyLink.value = true;
					tokenLink.value = data.value;
					ToastManager.showSuccess(toast, t('Squeeze.General.Success'), t('Squeeze.UserManagement.TokenCreated'));
				}
				else {
					ToastManager.showSuccess(toast, t('Squeeze.General.Success'), t('Squeeze.Login.SendMailSuccess'));
				}
				showDialog.value = false;
			}).catch(response => response.json().then ((err: { message: string }) => {
				ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
			})).finally(() => {
				loadingDialog.value = false;
			})
		}

		/** Triggered on click of the button */
		const onClickCopy = () => {
			tokenLinkRef.value.$el.select();
			document.execCommand('copy');
			ToastManager.showSuccess(toast, t('Squeeze.General.Success'), t('Squeeze.UserManagement.LinkCopied'));
		}

		return {
			t,
			toast,
			loading,
			tokenLinkRef,
			showValidationMessage,
			isValidationInvalid,
			user,
			showDialog,
			loadingDialog,
			store,
			showCopyLink,
			tokenLink,
			onUpdate,
			reloadData,
			sendResetMail,
			onClickCopy,
			saveUser,
			openResetDialog,
		};
	},
});

</script>

<style scoped>

</style>
